import {
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { UserContext } from "../../../../Context/UserContextAPI";
import useQueryFetch from "../../../../hooks/useQueryFetch";
import request from "../../../../utils/request";
import LoaderLayout from "../../../../components/Layout/LoaderLayout";
import toast from "react-hot-toast";
import { statusCheckHandler } from "./physicalStatusHandler";
import UserTableInReport from "../../../../components/Users/UserTableInReports";
import { useQueryClient } from "@tanstack/react-query";

const MedicalPhysical = ({ fromEntryPage, physicalEntryRef }) => {
  // params and query params
  const { userId } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const queryData = queryParams.get("data") || 3;
  const queryClient = useQueryClient();

  // context
  const { settingsData, accessPerm, currentUser } = useContext(UserContext);

  const detailsRef = useRef(null);

  const [physicalReportData, setPhysicalReportData] = useState([]);

  const [searchingId, setSearchingId] = useState("");

  const [invTestsData, setInvTestsData] = useState([]);

  const [isSaving, setIsSaving] = useState(false);

  // user detail data fetch
  const { data: userDetailData, isLoading } = useQueryFetch(
    ["user", userId],
    () => request.getOne("user-details/test", userId),
    {
      staleTime: 60 * 60 * 10, // 10 minutes
    }
  );
  const userDetail = userDetailData?.userDetail;

  // investigations data fetch
  const { data: investigations, isSuccess: isInvestigationsSuccess } =
    useQueryFetch(
      ["investigations", queryData, { role_id: userDetail?.user?.role_id }],
      () =>
        request.create(`reports/get-data/${queryData}`, {
          invIds:
            userDetail?.user?.role_id === 5
              ? userDetail?.investigation_id?.split(",")
              : userDetail?.package?.investigation_id?.split(",") || [],
        }),
      {
        placeholderData: [],
        enabled: !!userDetail?.user?.role_id,
        staleTime: 60 * 60 * 10, // 10 minutes
      }
    );

  // physical tests data fetch
  const { data: physicalTests } = useQueryFetch(
    ["reports/physical", userId],
    () => request.getOne("reports/physical", userId),
    {
      staleTime: 60 * 60 * 10, // 10 minutes
    }
  );

  // user test comments fetch
  const { data: userTestComments } = useQueryFetch(
    ["reports/get-physical-comments", userId],
    () => request.getOne("reports/get-physical-comments", userId),
    {
      staleTime: 60 * 60 * 10, // 10 minutes
    }
  );

  const [statusValue, setStatusValue] = useState(userTestComments?.status || 0);

  useEffect(() => {
    setStatusValue(userTestComments?.status);
  }, [userTestComments?.status]);

  // handler functions
  const handleOnChange = (event, testId) => {
    setPhysicalReportData((previousReport) => {
      const updatedReport = previousReport?.map((report) => {
        if (report?.test_id === testId) {
          return {
            ...report,
            result: event.target.value,
          };
        }
        return report;
      });
      return updatedReport;
    });

    // if (testId === 15 && event?.target?.value === "Normal") {
    //   setIfNormal(true);
    // } else {
    //   setIfNormal(false);
    // }
  };

  const fetchInvTestData = async (e, invTest) => {
    // for skin and physical remarks will work, otherwise it will reeturn null
    if (![14, 15].includes(invTest?.investigation_tests[0]?.id)) {
      setSearchingId(null);
      return null;
    }
    const dataToSend = {
      test_id: invTest?.investigation_tests[0]?.id,
      info_value: e.target.value.trim(),
    };
    if (dataToSend.test_id) {
      const response = await request.create(
        "reports/investigation-tests-data",
        dataToSend
      );
      if (response) {
        setInvTestsData(response);
      }
    }
  };

  const handleSelect = (value, index, testId) => {
    // setChangeColor("text-dark");
    setPhysicalReportData((previousReport) => {
      const updatedReport = previousReport?.map((report) => {
        if (report?.test_id === testId) {
          return {
            ...report,
            result: value,
          };
        }
        return report;
      });
      return updatedReport;
    });
    //
    setSearchingId(null);
    // console.log("handleSelect", value, index, testId);
  };

  const handleNormalValue = () => {
    investigations?.forEach((investigation, index) => {
      //   setChangeColor("text-dark");
      setPhysicalReportData((previousReport) => {
        const updatedReport = previousReport.map((report) => {
          if (report?.test_id === investigation?.investigation_tests[0]?.id) {
            if (investigation?.investigation_tests[0]?.info) {
              return {
                ...report,
                result: investigation?.investigation_tests[0]?.info?.replace(
                  /<[^>]*>/g,
                  ""
                ),
              };
            }
          }
          return report;
        });
        return updatedReport;
      });
    });
  };

  // use effects
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (detailsRef.current && !detailsRef.current.contains(event.target)) {
        setSearchingId(null);
      }
    };

    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [detailsRef.current]);

  useEffect(() => {
    // setPhysicalReportData([]);
    if (physicalTests?.length > 0) {
      const initialData = investigations?.map((investigation) => {
        // Find matching pathologyTest for the current investigation
        const matchedTest = physicalTests?.find(
          (test) => test.investigation_id === investigation.id
        );
        // Determine the result and test_id based on matchedTest or default to the first investigation_test
        const result =
          matchedTest?.result?.replace(/<[^>]*>/g, "") || matchedTest?.result;
        const test_id = matchedTest
          ? matchedTest.test_id
          : investigation.investigation_tests[0]?.id;

        return {
          user_id: userId,
          investigation_id: investigation.id,
          test_id: test_id,
          user_file: matchedTest ? matchedTest.user_file : "",
          result: result,
          riskFactor: "",
        };
      });
      setPhysicalReportData(initialData);
    } else {
      // If no fetched values, initialize pathologyReportData based on investigations
      const initialData = investigations?.map((investigation) => ({
        user_id: userId,
        investigation_id: investigation.id,
        test_id: investigation.investigation_tests[0].id,
        user_file: "",
        result: "",
        riskFactor: "",
      }));
      setPhysicalReportData(initialData);
    }
  }, [userId, physicalTests, investigations]);

  const changedInvTestSave = async () => {
    const changedData = physicalReportData
      ?.map((test) => {
        if (test?.test_id === 14 || test?.test_id === 15) {
          return {
            test_id: test?.test_id,
            info_value: test?.result.trim(),
          };
        }

        return null;
      })
      .filter((item) => item !== null);
    try {
      const res = await request.create(
        "reports/investigation-tests-data/save",
        changedData
      );
      console.log("save investigations test", res);
    } catch (error) {
      console.log(error);
    }
  };

  // save physical report

  const savePhysicalReport = async () => {
    if (![1, 2, 3, 4].includes(statusValue)) {
      return toast.error("Please select a status");
    }
    try {
      setIsSaving(true);
      const { riskFactor } = statusCheckHandler(physicalReportData);

      const response = await request.updateOne(
        "reports/save/physical/data/all",
        userId,
        { physicalReportData, statusValue, riskFactor }
      );
      // console.log("response", response);
      if (response?.status === "OK") {
        await request.getOne("reports/user-test-status", userId);

        // refetch the data
        // queryClient.invalidateQueries({
        //   queryKey: ["reports/get-physical-comments", userId],
        // });
        queryClient.invalidateQueries({
          queryKey: ["reports/physical", userId],
        });

        // set new status in the cache of physical comments
        queryClient.setQueryData(
          ["reports/get-physical-comments", userId],
          (old) => {
            return {
              ...old,
              status: statusValue,
            };
          }
        );
      }

      await changedInvTestSave();

      // toast.success("Physical Report Saved");
    } catch (error) {
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  // normal and save button handler ref from entry page
  useImperativeHandle(physicalEntryRef, () => ({
    handleNormalValue,
    savePhysicalReport,
  }));

  return (
    <LoaderLayout isPending={isLoading}>
      <div className="card p-3 medical-report">
        <div className="row mb-2 bg-primary text-white px-2 py-2">
          <h6 className="mb-0">
            Medical Physical Report for {userDetail?.user?.f_name}{" "}
            {userDetail?.user?.l_name} [{userDetail?.user?.reg_prefix || ""}
            {userDetail?.user?.registration_no}]
          </h6>
        </div>

        {/* user table info */}
        <div className="row justify-content-center h-100">
          {userDetail && !fromEntryPage && (
            <UserTableInReport
              user={{
                ...userDetail?.user,
                userDetail: userDetail,
              }}
            />
          )}
        </div>

        {/* input field */}

        <div className="row bg-primary mt-1 p-3 px-3 rounded-3 text-light">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: "16px",
            }}
          >
            {investigations?.map((investigation, index) => {
              const isMaleAndPregnancyTest =
                (userDetail?.gender?.name === "Male" ||
                  userDetail?.gender?.name === "male") &&
                investigation?.name === "Pregnancy Test";
              if (isMaleAndPregnancyTest) {
                return null;
              }
              const report = physicalReportData?.find(
                (report) =>
                  report?.test_id === investigation?.investigation_tests[0]?.id
              );
              // console.log("report", report);

              const { reports: colorReports } = statusCheckHandler(
                [
                  {
                    ...investigation,
                    investigation_id: investigation?.id,
                    result: report?.result,
                  },
                ],
                userId
              );
              const colorReport = colorReports[0];

              // Determine the text color based on status
              let textColorClass = "";
              if (colorReport) {
                if (colorReport.status === 2) {
                  // Fit
                  textColorClass = "text-success";
                } else if (colorReport.status === 3) {
                  // Unfit
                  textColorClass = "text-danger";
                } else if (colorReport.status === 4) {
                  // Held-up
                  textColorClass = "text-primary";
                }
              }
              return (
                <div key={index} className="d-flex align-items-center fs-6">
                  <label
                    htmlFor={`${investigation?.name?.toLowerCase()}`}
                    className="w-25"
                  >
                    {investigation?.name}
                  </label>
                  {investigation?.investigation_tests[0]?.value_type?.toLowerCase() ===
                  "dropdown" ? (
                    <select
                      name="dropdown"
                      value={report?.result}
                      className={`form-select w-75 ${textColorClass}`}
                      id={`${investigation?.name?.toLowerCase()}`}
                      onChange={(event) => {
                        handleOnChange(
                          event,
                          investigation?.investigation_tests[0]?.id
                        );
                      }}
                      autoComplete="off"
                    >
                      {investigation?.investigation_tests[0]?.dropdown_value
                        ?.split(",")
                        .map((v, index) => (
                          <option key={index} value={v}>
                            {v}
                          </option>
                        ))}
                    </select>
                  ) : (
                    <div className="w-75">
                      <div className="input-group w-100">
                        <input
                          type="text"
                          id={`${investigation?.name?.toLowerCase()}`}
                          value={report?.result}
                          className={`form-control w-100 ${textColorClass}`}
                          onChange={(event) => {
                            handleOnChange(
                              event,
                              investigation?.investigation_tests[0]?.id
                            );

                            setSearchingId(index);
                            fetchInvTestData(event, investigation);
                          }}
                          autoComplete="off"
                        />
                        {searchingId === index && invTestsData?.length > 0 && (
                          <div
                            className="position-absolute border-bottom top-100 bg-light text-dark w-100 z-10"
                            style={{
                              zIndex: "100",
                              left: "0%",
                              overflowY: "scroll",
                              maxHeight: "410px",
                              width: "100%",
                            }}
                            ref={detailsRef}
                          >
                            {invTestsData?.map((item) => (
                              <div
                                key={item?.id}
                                className="search-row"
                                onClick={(event) =>
                                  handleSelect(item?.info, index, item?.test_id)
                                }
                                style={{
                                  padding: "2px 12px",
                                  cursor: "pointer",
                                  width: "100%",
                                  color: "#000",
                                  fontWeight: "bold",
                                  fontSize: "16px",
                                }}
                              >
                                {item?.info}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: "16px",
            }}
          >
            <div className="d-flex align-items-center mt-4">
              <label htmlFor="risk-factor" className="w-25 fw-bold">
                Status
              </label>
              <select
                name="dropdown"
                className="form-select fw-bold w-75"
                id="risk-factor"
                onChange={(event) => setStatusValue(Number(event.target.value))}
                value={statusValue}
              >
                <option value="" className="fw-bold">
                  Select Status
                </option>
                <option value={2} className="fw-bold">
                  FIT
                </option>
                <option value={3} className="fw-bold">
                  UNFIT
                </option>
                <option value={4} className="fw-bold">
                  HELDUP
                </option>
              </select>
            </div>
          </div>
        </div>

        {/* next prev submit and normal buttons */}
        <div className="row mt-3">
          <div className="col-6">
            <div className="d-flex gap-2">
              {!fromEntryPage &&
                userDetailData?.nextPage &&
                settingsData?.next_prev_button === 1 && (
                  <Link
                    to={`/dashboard/medical-reports/physical/create/${userDetailData?.nextPage}?data=3`}
                    className="btn btn-primary"
                  >
                    Previous
                  </Link>
                )}
              {!fromEntryPage &&
                userDetailData?.previousPage &&
                settingsData?.next_prev_button === 1 && (
                  <Link
                    to={`/dashboard/medical-reports/physical/create/${userDetailData?.previousPage}?data=3`}
                    className="btn btn-primary"
                  >
                    Next
                  </Link>
                )}
            </div>
          </div>
          <div className="col-6">
            <div className="d-flex gap-2 justify-content-end">
              {accessPerm(33, 22) && (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleNormalValue}
                >
                  Set Normal
                </button>
              )}

              {!fromEntryPage && (
                <button
                  type="button"
                  className="btn btn-success px-2"
                  onClick={savePhysicalReport}
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <div className="d-flex align-items-center gap-2">
                      <span
                        className="spinner-border"
                        style={{ width: "1rem", height: "1rem" }}
                      />
                      <span>Saving...</span>
                    </div>
                  ) : (
                    "Save Results"
                  )}
                </button>
              )}

              {!fromEntryPage && (
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    window.close();
                  }}
                >
                  Close
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </LoaderLayout>
  );
};

export default MedicalPhysical;
